import { TicketStatus, TicketPriority } from "interfaces/entities/ticket.model";
import { DesignStatus, DesignPriority } from "./primitives";


export const statusToApp = (s: DesignStatus): TicketStatus => {
    switch (s) {
        case "open":
            return TicketStatus.NEW;
        case "progress":
            return TicketStatus.IN_PROGRESS;
        case "pending":
            return TicketStatus.WAITING_FOR_RESPONSE;
        case "resolved":
            return TicketStatus.RESOLVED;
        case "closed":
            return TicketStatus.CLOSED;
    }
};

// App uses LOW / MEDIUM / HIGH only (no urgent). We expose the design's
// `urgent` slot mapped to HIGH so the picker still shows four options;
// urgent + high are visually distinct but persist as the same backend value.
export const priorityFromApp = (p: TicketPriority | string | null | undefined): DesignPriority => {
    switch (p) {
        case TicketPriority.HIGH:
            return "high";
        case TicketPriority.MEDIUM:
            return "normal";
        case TicketPriority.LOW:
            return "low";
        default:
            return "normal";
    }
};

export const priorityToApp = (p: DesignPriority): TicketPriority => {
    switch (p) {
        case "urgent":
        case "high":
            return TicketPriority.HIGH;
        case "normal":
            return TicketPriority.MEDIUM;
        case "low":
            return TicketPriority.LOW;
    }
};

// SLA-цели на решение тикета по приоритету (в часах).
// Совпадают с дефолтами бэка в tickets.controller.ts → SLA_RESOLUTION_HOURS,
// которые используются виджетами «SLA-нарушители» и «Очередь». Если бэк
// начнёт отдавать пер-org конфиг — заменить эти константы вызовом API.
export const SLA_RESOLUTION_HOURS: Record<"HIGH" | "MEDIUM" | "LOW", number> = {
    HIGH: 8,
    MEDIUM: 24,
    LOW: 72,
};

// Целевое время первого ответа (в часах). Бэк отдельную метрику в дефолтах
// не хранит — берём 1/8 от срока решения, что соответствует общепринятой
// практике helpdesk (для HIGH 8ч это 1ч до первого ответа).
const FIRST_RESPONSE_FRACTION = 1 / 8;

export type SlaPriorityKey = keyof typeof SLA_RESOLUTION_HOURS;

const priorityKey = (p: DesignPriority): SlaPriorityKey => {
    switch (p) {
        case "urgent":
        case "high":
            return "HIGH";
        case "low":
            return "LOW";
        case "normal":
        default:
            return "MEDIUM";
    }
};

export const slaResolutionSeconds = (p: DesignPriority): number =>
    SLA_RESOLUTION_HOURS[priorityKey(p)] * 3600;

export const slaFirstResponseSeconds = (p: DesignPriority): number =>
    Math.round(SLA_RESOLUTION_HOURS[priorityKey(p)] * 3600 * FIRST_RESPONSE_FRACTION);

export type SlaState = "on-track" | "warning" | "breached" | "met";

// Состояние оставшегося времени до дедлайна: «в норме / предупреждение / просрочено».
// Пороги — относительно цели: <25% запаса → warning, <0 → breached.
export const computeSlaState = (
    secondsLeft: number,
    targetSeconds: number
): { state: SlaState; label: string } => {
    if (secondsLeft <= 0) {
        return { state: "breached", label: `Просрочено на ${formatDuration(-secondsLeft)}` };
    }
    const ratio = secondsLeft / Math.max(1, targetSeconds);
    if (ratio < 0.25) return { state: "warning", label: `Осталось ${formatDuration(secondsLeft)}` };
    return { state: "on-track", label: `Осталось ${formatDuration(secondsLeft)}` };
};

// Форматирует длительность для всех SLA-меток. Часы и минуты для интервалов
// от часа, иначе минуты и секунды — секунды важны на финальной минуте
// перед нарушением.
export const formatDuration = (s: number) => {
    const v = Math.max(0, Math.floor(s));
    const h = Math.floor(v / 3600);
    const m = Math.floor((v % 3600) / 60);
    const sec = v % 60;
    if (h > 0) return `${h}ч ${m}м`;
    return `${m}м ${sec.toString().padStart(2, "0")}с`;
};

// Maps the design's status keys onto the existing TICKET_STATUS_TRANSITIONS
// table. Centralises the rule "did the transition the user picked match
// what the backend allows" so the pickers can disable invalid moves.
export { isTicketTransitionAllowed } from "interfaces/entities/ticket.model";
